import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type StickyRow = { id: string; rowsCount: number; rowIdx: number };

interface UseTableStickyHeaderProps<T> {
  apiRef: React.MutableRefObject<GridApiPro>;
  rows: T[];
  estimatedRowHeight?: number;
  debounceMs?: number;
}

export const useTableStickyHeader = <T extends StickyRow>({
  apiRef,
  rows,
  estimatedRowHeight = 45,
  debounceMs = 28,
}: UseTableStickyHeaderProps<T>) => {
  const [stickyRowId, setStickyRowId] = useState<string | null>(null);
  const stickyRowIdRef = useRef<string | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef(false);

  const stickyRows = useMemo(() => {
    const stickyRow = rows.find((row) => row.id === stickyRowId);
    if (!stickyRow) return;

    const stickyRowWithId = {
      ...stickyRow,
      id: `sticky-${stickyRow.id}`,
    };

    return {
      top: [stickyRowWithId],
    };
  }, [stickyRowId, rows]);

  const handleScroll = useCallback(() => {
    if (!apiRef.current || !rows.length || isUpdatingRef.current) {
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      try {
        const scrollTop = apiRef.current?.getScrollPosition?.()?.top || 0;
        const currentScrollRowIndex = Math.floor(scrollTop / estimatedRowHeight);

        let newStickyRowId = null;

        for (const row of rows) {
          const categoryEndIndex = row.rowIdx + row.rowsCount;

          if (currentScrollRowIndex > row.rowIdx && currentScrollRowIndex <= categoryEndIndex) {
            newStickyRowId = row.id;
            break;
          }
        }

        if (newStickyRowId !== stickyRowIdRef.current) {
          isUpdatingRef.current = true;
          stickyRowIdRef.current = newStickyRowId;

          setTimeout(() => {
            setStickyRowId(newStickyRowId);
            isUpdatingRef.current = false;
          }, 0);
        }
      } catch (error) {
        console.warn('Error in scroll handler:', error);
        isUpdatingRef.current = false;
      }
    }, debounceMs);
  }, [apiRef, rows, debounceMs, estimatedRowHeight]);

  useEffect(() => {
    if (!apiRef.current) return;

    const unsubscribe = apiRef.current.subscribeEvent?.('scrollPositionChange', handleScroll);
    return () => {
      unsubscribe?.();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [apiRef, handleScroll]);

  return {
    stickyRows,
    stickyRowId,
  };
};
